import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import apiClient from '@app/config/axios.config'
import { MapLayer } from '@app/redux/riskMap/riskMapSlice'
import { POLL_INTERVAL_MS } from '../Live.config'
import { useTransformLiveLayers } from '../hooks/useTransformLiveLayers'
import { BASE_API_URL } from '@app/app-constants'
import { LiveContextInterface, LiveLayer } from '../Live.types'
import { Snackbar, useSnackbars } from '@app/contexts/SnackbarContext'

const LiveContext = createContext<LiveContextInterface>({
  liveLayers: [],
  setLiveLayers: () => {},
  toggleLiveFeed: () => {},
  activeLiveLayers: [],
  setActiveLiveLayers: () => {},
  liveData: {},
  refreshFeed: async () => {},
  riskLayers: [],
  riskLayerVisibility: {},
  toggleRiskLayer: () => {},
  liveMapSelected: false,
  setLiveMapSelected: () => {},
})

export function LiveContextProvider({ children }: { children: React.ReactNode }) {
  const { transformLiveFeedToGeoJSON } = useTransformLiveLayers()
  const { displaySnackbar, removeSnackbar } = useSnackbars()

  // Hold snackbar actions in a ref so the loading effect only re-runs when liveLayersLoading
  // changes. displaySnackbar/removeSnackbar change identity every time the snackbars array
  // changes, which would cause an infinite loop if put in the effect's dep array.
  const snackbarActionsRef = useRef({ displaySnackbar, removeSnackbar })
  useEffect(() => {
    snackbarActionsRef.current = { displaySnackbar, removeSnackbar }
  })

  const [liveLayers, setLiveLayers] = useState<LiveLayer[]>([])
  const [activeLiveLayers, setActiveLiveLayers] = useState<LiveLayer[]>([])
  const [liveData, setLiveData] = useState<Record<string, GeoJSON.FeatureCollection | null>>({})
  const [liveLayersLoading, setLiveLayersLoading] = useState<boolean>(false)
  const [riskLayers, setRiskLayers] = useState<MapLayer[]>([])
  const [riskLayerVisibility, setRiskLayerVisibility] = useState<Record<string, boolean>>({})
  const [liveMapSelected, setLiveMapSelected] = useState<boolean>(false)

  useEffect(() => {
    setLiveData((prev) => Object.fromEntries(liveLayers.map((l) => [l.id, prev[l.id] ?? null])))
  }, [liveLayers])

  useEffect(() => {
    apiClient
      .get<MapLayer[]>('/layer')
      .then((res) => {
        const layers = Array.isArray(res.data) ? res.data : []
        setRiskLayers(layers)
        setRiskLayerVisibility(Object.fromEntries(layers.map((l) => [l.id, false])))
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!liveLayersLoading) return
    const msg: Snackbar = { message: 'Loading...', type: 'info' }
    snackbarActionsRef.current.displaySnackbar(msg)
    return () => {
      snackbarActionsRef.current.removeSnackbar(msg)
    }
  }, [liveLayersLoading])

  const fetchFeed = useCallback(
    async (layer: LiveLayer) => {
      if (!layer.url) return

      try {
        let url = layer.url
        if (layer.proxy_url)
          url = layer.url.replace(
            layer.proxy_url,
            `${BASE_API_URL}/layer/live_feeds/${layer.id}/proxy/`,
          )
        const response = await fetch(url, { credentials: layer.proxy_url ? 'include' : undefined })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const data = await transformLiveFeedToGeoJSON(layer, response)

        setLiveLayers((prev) =>
          prev.map((l) =>
            l.id === layer.id ? { ...l, lastUpdated: new Date(), isError: false } : l,
          ),
        )
        setLiveData((prev) => ({ ...prev, [layer.id]: data }))
      } catch {
        // on first load, set empty data so the loading state clears; subsequent poll failures keep the last successful data
        setLiveData((prev) => ({
          ...prev,
          [layer.id]: prev[layer.id] ?? { type: 'FeatureCollection', features: [] },
        }))
        setLiveLayers((prev) => prev.map((l) => (l.id === layer.id ? { ...l, isError: true } : l)))
      }
    },
    [transformLiveFeedToGeoJSON],
  )

  useEffect(() => {
    if (activeLiveLayers.length === 0) {
      setLiveLayersLoading(false)
      return
    }

    let cancelled = false
    setLiveLayersLoading(true)

    // Track when all initial fetches resolve, then clear loading.
    // The poll interval re-fetches silently without resetting the loading state.
    const fetchPromises = activeLiveLayers.map((layer) => fetchFeed(layer))
    Promise.all(fetchPromises).then(() => {
      if (!cancelled) setLiveLayersLoading(false)
    })

    const interval = setInterval(() => {
      activeLiveLayers.forEach((layer) => fetchFeed(layer))
    }, POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [activeLiveLayers, fetchFeed])

  const refreshFeed = useCallback(
    async (id: string) => {
      const layer = activeLiveLayers.find((l) => l.id === id) ?? liveLayers.find((l) => l.id === id)
      if (layer) await fetchFeed(layer)
    },
    [fetchFeed, activeLiveLayers, liveLayers],
  )

  const toggleLiveFeed = useCallback((id: string) => {
    setActiveLiveLayers((prev) =>
      prev.map((f) => (f.id === id ? { ...f, visible: !f.visible } : f)),
    )
  }, [])

  const toggleRiskLayer = useCallback((id: string) => {
    setRiskLayerVisibility((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  return (
    <LiveContext.Provider
      value={{
        liveLayers,
        setLiveLayers,
        toggleLiveFeed,
        activeLiveLayers,
        setActiveLiveLayers,
        liveData,
        refreshFeed,
        riskLayers,
        riskLayerVisibility,
        toggleRiskLayer,
        liveMapSelected,
        setLiveMapSelected,
      }}
    >
      {children}
    </LiveContext.Provider>
  )
}

export const useLiveContext = () => useContext(LiveContext)
